import { Component, ElementRef, OnInit, ViewChild, ViewChildren, QueryList } from '@angular/core';
import { ActivatedRoute}  from '@angular/router';

import { ChartConfiguration, ChartData, ChartType, Plugin } from 'chart.js';

import { BaseChartDirective } from 'ng2-charts';

import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { faGauge, faPieChart, faQuestionCircle, faBell, faCalendarDays, faClock, faCheck } from '@fortawesome/free-solid-svg-icons';

import { DashboardService } from '../shared/dashboard.service';
import { UtilityService } from '../../../../core/services/utility.service';
import { ManagementUser } from '../../../../core/models/management-user.model';
import { SessionStorageService } from '../../../../core/services/session-storage.service';
import { District } from '../../../../core/models/district.model';
import { School } from '../../../../core/models/school.model';
import { LoadingService } from '../../../../core/services/loading.service';

import { systemDashboardIntroSteps } from './system-dashboard.intro-steps';
import { canViewCoTeacherFilter, canViewSchoolFilter } from 'src/app/reports/utils';
import { CATEGORY_ICONS } from 'src/app/shared/standards/icons';

interface UsageBuckets {
  over60: any[] ;
  mid: any[] ;
  under30: any[] ;
}

@Component({
    selector: 'wf-dashboard-school',
    templateUrl: './system-dashboard.component.html',
    styleUrls: ['./system-dashboard.component.css'],
    standalone: false
})
export class SystemDashboardComponent implements OnInit {
  currentUser: ManagementUser | null = null ;
  schoolFilterError: string = '' ;
  showSchoolFilter: boolean = false ;
  hideSharedStudents: boolean = false;
  showCoTeacherFilter: boolean = false ;
  canViewNotifications: boolean = true;

  // Daily Notifications
  celebrationsCount: number = 0 ;
  needAttentionCount: number = 0 ;

  // Minutes Usage
  selectedUsageWeek: 'current' | 'previous' = 'current' ;
  currentWeekSessions: any[] = [] ;
  previousWeekSessions: any[] = [] ;
  usageBuckets: UsageBuckets = { over60: [], mid: [], under30: [] } ;

  // Unit Placement
  mostCommonUnits: any[] = [] ;

  districts: District[] = [] ;
  schools: School[] = [] ;
  teachers: ManagementUser[] = [] ;

  reportsIcon = CATEGORY_ICONS['class-reports'];
  dashboardIcon = faGauge ;
  reportIcon = faPieChart ;
  helpIcon = faQuestionCircle ;
  notificationsIcon = faBell ;
  remindersIcon = faCalendarDays ;
  usageIcon = faClock ;
  checkIcon = faCheck ;

  readonly tourSteps = systemDashboardIntroSteps;

  // The six objectives each cover four of the twenty-four units. Bars and the
  // legend are colored by the objective the unit belongs to.
  objectiveColors: string[] = [ '#0095DA', '#B2D235', '#7F7F7F', '#0054A6', '#80C342', '#007236' ] ;

  // Background highlight shading for each objective's region on the x-axis -
  // a light tint of the matching objective bar color (objectiveColors).
  objectiveShading: string[] = [ '#E6F4FB', '#F1F8DC', '#ECECEC', '#E6EDF7', '#EDF6E1', '#E2F0E9' ] ;

  // Minutes Usage pie chart (matches the three usage range colors)
  pieChartType: 'pie' = 'pie' ;
  pieChartData: ChartData<'pie'> = {
    labels: [ '60+ Minutes', '30 - 60 Minutes', '0 - 30 Minutes' ],
    datasets: [
      {
        data: [ 0, 0, 0 ],
        backgroundColor: [ '#80C342', '#F2AE43', '#DB524B' ],
      }
    ]
  } ;
  pieChartOptions: ChartConfiguration<'pie'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      datalabels: { display: false },
      legend: { display: false },
    },
  } ;

  // Static weekly reminder content. The card shows the short title/text; the
  // "View Details" dialog shows the full detail plus a "where to look" link.
  // NOTE: copy is transcribed from the mockups - confirm final wording with
  // product before this is considered done.
  weeklyReminders = [
    {
      cardTitle: 'Student Usage:',
      cardText: 'Check your Group Summary report.',
      detailTitle: 'Student Usage',
      detailBody: 'Review current week usage to determine whether students are meeting their weekly time goal of 60-80 minutes.',
      whereToLookLabel: 'Reports - Progress Monitoring:',
      linkText: 'Group Summary',
      linkRoute: [ '/reports/system/summary/group' ],
    },
    {
      cardTitle: 'Student Motivation:',
      cardText: 'Celebrate student success!',
      detailTitle: 'Student Motivation: Celebrate student success!',
      detailBody: 'Check in on your class or student motivation system and celebrate progress toward goals. This could include recognizing effort during a 20 minute session, completion of levels or units, growth in skills, or completion of weekly time goals.',
      whereToLookLabel: 'Student Engagement:',
      linkText: 'Student Goal Setting',
      linkRoute: [ '/student-engagement', 'system', 'all', 'Student Goal Setting' ],
    },
    {
      cardTitle: 'Reading Aloud:',
      cardText: 'Students read aloud each week.',
      detailTitle: 'Reading Aloud: Students read aloud each week.',
      detailBody: 'Print WordFlight passages and use classroom texts to practice fluent reading aloud. Students can read aloud with partners, teachers, and family members.',
      whereToLookLabel: 'Small Group Instruction:',
      linkText: 'Connected Texts',
      linkRoute: [ '/small-group', 'system', 'all', 'Poems & Passages' ],
    },
  ] ;

  // Draws the per-objective label band beneath the x-axis, the Pre/Mid/Post
  // diagnostic dividers, and the diagnostic labels above those dividers.
  unitPlacementPlugin: Plugin<'bar'> = {
    id: 'unitPlacementDecorations',
    afterDraw: (chart) => {
      if (chart.canvas.id !== 'unit-placement-chart') return ;

      const ctx = chart.ctx ;
      const chartArea = chart.chartArea ;
      const xScale = chart.scales['x'] ;
      if (!xScale) return ;

      const halfStep = (xScale.getPixelForValue(1) - xScale.getPixelForValue(0)) / 2 ;
      const midX = (xScale.getPixelForValue(11) + xScale.getPixelForValue(12)) / 2 ;

      const drawLine = (x: number) => {
        ctx.save() ;
        ctx.beginPath() ;
        ctx.moveTo(x, chartArea.top) ;
        ctx.lineTo(x, chartArea.bottom) ;
        ctx.lineWidth = 3 ;
        ctx.strokeStyle = '#555555' ;
        ctx.stroke() ;
        ctx.restore() ;
      } ;

      // Pre (left edge), Mid (between units 12 and 13), Post (right edge)
      drawLine(chartArea.left) ;
      drawLine(midX) ;
      drawLine(chartArea.right) ;

      // Diagnostic labels above each divider line
      ctx.save() ;
      ctx.fillStyle = '#555555' ;
      ctx.font = '700 11px "Gotham SSm A", "Gotham SSm B", "Arial", "Helvetica", sans-serif' ;
      const topLineY = chartArea.top - 20 ;
      const bottomLineY = chartArea.top - 8 ;
      const drawLabel = (text: string, x: number, align: CanvasTextAlign) => {
        ctx.textAlign = align ;
        ctx.fillText(text, x, topLineY) ;
        ctx.fillText('Diagnostic', x, bottomLineY) ;
      } ;
      drawLabel('Pre', chartArea.left + 4, 'left') ;
      drawLabel('Mid', midX, 'center') ;
      drawLabel('Post', chartArea.right - 4, 'right') ;
      ctx.restore() ;

      // Objective label cells beneath the unit-number ticks
      const bandTop = xScale.bottom + 4 ;
      const bandHeight = 22 ;
      ctx.save() ;
      ctx.textAlign = 'center' ;
      ctx.textBaseline = 'middle' ;
      ctx.font = '700 12px "Gotham SSm A", "Gotham SSm B", "Arial", "Helvetica", sans-serif' ;
      for (let group = 0; group < this.objectiveColors.length; group++)
      {
        const left = Math.max(chartArea.left, xScale.getPixelForValue(group * 4) - halfStep) ;
        const right = Math.min(chartArea.right, xScale.getPixelForValue(group * 4 + 3) + halfStep) ;
        ctx.fillStyle = this.objectiveShading[group] ;
        ctx.fillRect(left, bandTop, right - left, bandHeight) ;
        ctx.fillStyle = this.objectiveColors[group] ;
        ctx.fillText(`Objective ${group + 1}`, (left + right) / 2, bandTop + bandHeight / 2) ;
      }
      ctx.restore() ;
    }
  } ;

  chartPlugins = [ this.unitPlacementPlugin ] ;
  chartType: ChartType = 'bar' ;
  chartData: ChartData<'bar'> = {
    labels: Array.from(Array(24).keys()).map(k => `${k + 1}`),
    datasets: [
      {
        data: [],
        backgroundColor: Array(24).fill(null).map((value, idx) => { return this.objectiveColors[Math.floor(idx / 4)] ; }),
        hoverBackgroundColor: 'rgba(0, 0, 0, 0.5)',
      }
    ]
  } ;
  chartOptions: ChartConfiguration['options'] = {
    maintainAspectRatio: false,
    responsive: true,
    layout: {
      padding: {
        top: 36,
        bottom: 32,
      },
    },
    scales: {
      y: {
        title: {
          display: true,
          text: 'Number of Students',
          font: {
            weight: '700',
            family: '"Gotham SSm A", "Gotham SSm B", "Arial", "Helvetica", sans-serif',
          },
        },
        grid: {
          display: false,
        },
        min: 0,
        ticks: {
          stepSize: 1,
          font: {
            family: '"Gotham SSm A", "Gotham SSm B", "Arial", "Helvetica", sans-serif',
          }
        }
      },
      x: {
        beginAtZero: true,
        title: {
          display: false,
          text: 'Unit Number',
          font: {
            weight: '700',
            family: '"Gotham SSm A", "Gotham SSm B", "Arial", "Helvetica", sans-serif',
          },
        },
        ticks: {
          stepSize: 1,
          font: {
            family: '"Gotham SSm A", "Gotham SSm B", "Arial", "Helvetica", sans-serif',
          }
        },
      },
    },
    plugins: {
      datalabels: {
        display: false,
      },
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
        external:(context) => {
          // NOTE: This logic all assume we have a single dataset (all the array reads to the 0-th index)
          let tooltipEl = document.getElementById('chartjs-tooltip');

          // Create element on first render
          if (!tooltipEl)
          {
            tooltipEl = document.createElement('div');
            tooltipEl.id = 'chartjs-tooltip';
            tooltipEl.classList.add('chartjs-tooltip') ;
            document.body.appendChild(tooltipEl);
          }

          // Hide if no tooltip
          const tooltipModel = context.tooltip ;
          if (tooltipModel.opacity === 0)
          {
            tooltipEl.style.opacity = '0' ;
            return;
          }

          // Set caret Position
          tooltipEl.classList.remove('above', 'below', 'no-transform');
          if (tooltipModel.yAlign)
          {
            tooltipEl.classList.add(tooltipModel.yAlign);
          }
          else
          {
            tooltipEl.classList.add('no-transform');
          }

          // Set Text
          if (tooltipModel.dataPoints && tooltipModel.body[0].lines && tooltipModel.body[0].lines.length)
          {
            // construct our label tile
            let colors = tooltipModel.labelColors[0] ;
            let style = 'background-color: ' + colors.backgroundColor ;

            let innerHtml = '<div>' ;
            innerHtml += '  <p class="chartjs-tooltip-header">' ;
            innerHtml += '    <span class="chartjs-tooltip-key" style="' + style + '"></span>' ;
            innerHtml += '    Unit ' + tooltipModel.dataPoints[0].label
            innerHtml += '  </p>' ;
            innerHtml += '  <div>' ;
            innerHtml += '    <p>' + tooltipModel.dataPoints[0].formattedValue + (tooltipModel.dataPoints[0].formattedValue === '1' ? ' student' : ' students') + '</p>' ;

            // We only display the students name if the teacher is logged in
            if ([ 'ROLE_TEACHER', 'ROLE_TEACHER_CURRICULUM', 'ROLE_TEACHER_REVIEW' ].includes(this.currentUser!.userRole.toUpperCase()))
            {
              innerHtml += '    <ul class="unit-students">';
              this.mostCommonUnits[tooltipModel.dataPoints[0].dataIndex].forEach((student: any) => {
                innerHtml += '      <li>' + student + '</li>';
              }) ;
              innerHtml += '    </ul>';
            }

            innerHtml += '  <div>' ;
            innerHtml += '</div>' ;

            tooltipEl.innerHTML = innerHtml ;
          }

          const position = context.chart.canvas.getBoundingClientRect();

          // Display, position, and set styles for font
          tooltipEl.style.opacity = '1';
          tooltipEl.style.position = 'absolute';
          tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + 'px';
          tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY + 'px';
          tooltipEl.style.padding = tooltipModel.options.padding + 'px ' + tooltipModel.options.padding + 'px';
          tooltipEl.style.pointerEvents = 'none';
        }
      }
    },
  } ;

  @ViewChildren(BaseChartDirective) charts?: QueryList<BaseChartDirective> ;
  @ViewChild('remindersmodal') remindersModal?: ElementRef ;
  @ViewChild('usagelistmodal') usageListModal?: ElementRef ;

  // The range whose student list is shown in the usage "View List" modal
  selectedUsageRangeLabel: string = '' ;
  selectedUsageRangeStudents: any[] = [] ;

  constructor(
    private route: ActivatedRoute,
    private modalService: NgbModal,
    private sessionStorageService: SessionStorageService,
    private dashboardService: DashboardService,
    private utilityService: UtilityService,
    private loadingService: LoadingService,
  ) {}

  ngOnInit(): void {
    this.currentUser = this.sessionStorageService.userData() ;
    this.showSchoolFilter = canViewSchoolFilter(this.currentUser);
    this.showCoTeacherFilter = canViewCoTeacherFilter(this.currentUser);

    if (this.route.snapshot.data['resolveData'].erred)
    {
      this.schoolFilterError = this.route.snapshot.data['resolveData'].message ;
    }
    else
    {
      this.applyDashboardData(this.route.snapshot.data['resolveData']) ;
    }

    // Filter data
    this.districts = this.route.snapshot.data['filterData'].districts ;
    this.schools = this.route.snapshot.data['filterData'].schools ;
    this.teachers = this.route.snapshot.data['filterData'].teachers ;

    if (this.currentUser!.isFILUser() || this.currentUser!.isDistrictUser())
    {
      // The FIL user and District user roles can select all schools
      this.schools.unshift({ schoolID: 0, districtID: 0, name: 'All', enabled: true }) ;
    }

    if (canViewSchoolFilter(this.currentUser))
    {
      // The FIL user and District and School user roles can select all teachers
      this.teachers.unshift(ManagementUser.getGenericUser()) ;
    }

    this.canViewNotifications = this.currentUser?.isTeacherUser() ?? false;
  }

  /**
   * Map a dashboard data payload onto the component state shared by the
   * initial resolve, the school/teacher filter, and the refresh button.
   */
  private applyDashboardData(data: any): void {
    this.schoolFilterError = '' ;
    this.celebrationsCount = data['dailyNotifications']?.celebration ?? 0 ;
    this.needAttentionCount = data['dailyNotifications']?.attention ?? 0 ;
    this.previousWeekSessions = data['previousWeekSessions'] ?? [] ;
    this.currentWeekSessions = data['currentWeekSessions'] ?? [] ;
    this.updateUsageBuckets() ;

    this.mostCommonUnits = data['mostCommonUnit'] ;
    this.chartData.datasets[0].data = data['mostCommonUnit'].map((value: any) => { return value.length ; }) ;
    this.updateCharts() ;
  }

  private updateCharts(): void {
    this.charts?.forEach((chart) => chart.update()) ;
  }

  /**
   * Group the selected week's sessions into the three usage ranges
   * (60+, 30-60, and 0-30 minutes) shown in the Minutes Usage card.
   */
  updateUsageBuckets(): void {
    const sessions = this.selectedUsageWeek === 'current' ? this.currentWeekSessions : this.previousWeekSessions ;
    const buckets: UsageBuckets = { over60: [], mid: [], under30: [] } ;

    sessions.forEach((session) => {
      const minutes = this.utilityService.getDisplayedSessionTime(session.sessionTime) ;
      if (minutes >= 60)
      {
        buckets.over60.push(session) ;
      }
      else if (minutes >= 30)
      {
        buckets.mid.push(session) ;
      }
      else
      {
        buckets.under30.push(session) ;
      }
    }) ;

    this.usageBuckets = buckets ;
    this.pieChartData.datasets[0].data = [ buckets.over60.length, buckets.mid.length, buckets.under30.length ] ;
    this.updateCharts() ;
  }

  get totalUsageStudents(): number {
    return this.usageBuckets.over60.length + this.usageBuckets.mid.length + this.usageBuckets.under30.length ;
  }

  usagePercent(count: number): number {
    const total = this.totalUsageStudents ;
    return total ? (count / total) * 100 : 0 ;
  }

  showUsageList(label: string, students: any[]): void {
    this.selectedUsageRangeLabel = label ;
    this.selectedUsageRangeStudents = [...students].sort(this.utilityService.sortStudentsTimeGoals.bind(this.utilityService)) ;
    this.modalService.open(this.usageListModal, { animation: true, centered: true, scrollable: true, windowClass: 'dashboard-modal' }) ;
  }

  showReminderDetails(): void {
    this.modalService.open(this.remindersModal, { animation: true, centered: true, scrollable: true, size: 'lg', windowClass: 'dashboard-modal reminders-modal' }) ;
  }

  printReminders(): void {
    // Isolate the dialog so only its content prints (see the print styles keyed
    // off the "printing-reminders" body class). Cleaned up once printing ends.
    const cleanup = () => {
      document.body.classList.remove('printing-reminders') ;
      window.removeEventListener('afterprint', cleanup) ;
    } ;
    document.body.classList.add('printing-reminders') ;
    window.addEventListener('afterprint', cleanup) ;
    window.print() ;
  }

  getDisplaySessionTime(sessionTime: number) {
    return this.utilityService.getDisplayedSessionTime(sessionTime) ;
  }

  filterSchoolTeacher(filterOptions: any) {
    this.dashboardService.getSystemDashboardData(!this.hideSharedStudents).subscribe({
      next: (data: any) => {
        if (data.erred)
        {
          this.schoolFilterError = data.message ;
        }
        else
        {
          this.applyDashboardData(data) ;
        }
      }
    })
  }

  refreshDashboardData() {
    this.loadingService.start() ;
    this.dashboardService.getSystemDashboardData(!this.hideSharedStudents).subscribe({
      next: (data: any) => {
        if (data.erred)
        {
          this.schoolFilterError = data.message ;
        }
        else
        {
          this.loadingService.finish();
          this.applyDashboardData(data) ;
        }
      }
    })
  }

  toggleSharedStudents() {
    this.hideSharedStudents = !this.hideSharedStudents;
    this.refreshDashboardData();
  }
}
